// FR-RD14: a recording interrupted by a crash, restart, closed tab or lost network uploads
// and publishes on next launch without user action. This file is the shared vocabulary for
// that: the persisted shape every platform's local store writes, and the dependency-injected
// ports `runRecovery` (./stateMachine.ts) is built against so it is unit-testable with fakes
// and reusable across the web IndexedDB adapter and the desktop/mobile disk adapter.
import type { UploadQueueCompletedPart } from "../uploadQueue";

export type RecoverySessionStatus = "recording" | "pending_upload" | "uploading" | "failed";

/** Persisted after session creation and after every accepted part (brief scope item 1).
 * `uploadId` is always `grackleId` here — the multipart upload and the `grackles` row it
 * belongs to share one id (server/src/uploads.rs's `CreateUploadResp.grackle_id`) — kept as a
 * separate field because the two concepts are distinct even though the value never differs. */
export interface PersistedUploadState {
  localSessionId: string;
  /** Who recorded it. Recovery only resumes sessions recorded by the signed-in user, so a
   * shared browser/machine never uploads (or offers to export) someone else's recording. */
  userId?: string;
  orgId: string;
  title: string;
  contentType: "video/webm" | "video/mp4";
  grackleId: string | null;
  uploadId: string | null;
  partSize: number;
  completedParts: UploadQueueCompletedPart[];
  finalized: boolean;
  status: RecoverySessionStatus;
  lastError?: string;
  updatedAt: number;
  /** Written only by the live recorder (livePersist.ts), on every persist and every few
   * seconds while capturing. Recovery's own writes carry it along unchanged, so a fresh value
   * means "a live recorder is still writing this", never "a recovery attempt touched it". */
  liveHeartbeatAt?: number;
}

/** One platform's local storage, adapted to the shape the state machine needs. Web implements
 * this over IndexedDB (persist/chunkStore.ts via recovery/webAdapter.ts); desktop/mobile over
 * disk.rs's segments plus the upload.json sidecar (recovery/diskAdapter.ts). */
export interface LocalSource {
  /** Local session ids with recoverable data, in the order they should be processed —
   * recovery runs one at a time, in order (brief scope item 2). */
  list(): Promise<string[]>;
  /** Every chunk for this session, in capture order. Rejects if a chunk/segment is unreadable
   * (corrupt or missing) — the state machine treats that as a failed, not retryable, session
   * and preserves the data rather than losing it. */
  readChunks(id: string): Promise<Blob[]>;
  loadState(id: string): Promise<PersistedUploadState | null>;
  saveState(id: string, state: PersistedUploadState): Promise<void>;
  /** Marks the session failed (status + lastError) without touching its data — the recovery
   * UI offers "Export file" / "Discard" for a session in this state. */
  markFailed(id: string, err: string): Promise<void>;
  /** Deletes local data for one session. Called only after `complete` has succeeded (or a
   * 404 proved an earlier run's `complete` already succeeded) — never on failure. */
  deleteLocal(id: string): Promise<void>;
}

/** The result shape `RecoveryQueueHandle.finish()` resolves with — just enough of
 * `GrackleOut` for the state machine and its callers, not the whole server type. */
export interface RecoveryUploadResult {
  grackleId: string;
  status: string;
}

export interface RecoveryQueueHandle {
  push(chunk: Blob): void;
  finish(): Promise<RecoveryUploadResult>;
  /** Stops this queue locally (no further requests, no `complete`) without aborting the
   * server-side upload, so the next recovery run can resume it. Called when an attempt is
   * abandoned — a queue left running behind the state machine's back could still `complete`
   * the grackle later, racing the next attempt for the same session. */
  halt?(): void;
}

export interface QueueFactoryArgs {
  orgId: string;
  title: string;
  contentType: "video/webm" | "video/mp4";
  /** Set when resuming a session that already has a server-side upload. */
  resumeSeed: { grackleId: string; partSize: number; completedParts: UploadQueueCompletedPart[] } | null;
  onSessionCreated: (grackleId: string, partSize: number) => void;
  onPartAccepted: (part: UploadQueueCompletedPart) => void;
  /** The local session this queue uploads — lets the caller attribute progress snapshots. */
  sessionId?: string;
}

/** Builds the thing that actually talks to the network for one session. The real
 * implementation wraps `UploadQueue`; tests inject a fake (see stateMachine.test.ts). */
export type QueueFactory = (args: QueueFactoryArgs) => RecoveryQueueHandle;

export interface RecoveryApi {
  /**
   * Disambiguates a terminal failure on a *resumed* upload (brief scope item 1): every
   * `/uploads/{id}/*` route 404s identically once a grackle leaves `status='uploading'`,
   * whether that's because an earlier run's `complete` already succeeded (crash happened
   * right after) or because the multipart session genuinely expired/was never ours. `null`
   * means gone/forbidden (404) — treat as expired. A non-null status that isn't `"uploading"`
   * means `complete` landed — but not necessarily with everything this device holds: another
   * queue (a racing recovery sweep) can complete the same grackle from a truncated prefix of
   * the chunks. `sourceBytes` is what the provider actually stored; recovery only treats the
   * upload as finished when it covers every local byte.
   */
  getGrackleStatus(grackleId: string): Promise<{ status: string; sourceBytes: number | null } | null>;
}

/** Thrown by a `RecoveryQueueHandle` (or detected by the caller before even trying) to mean
 * "the network is the problem, not this upload" — the state machine leaves the session as
 * `pending_upload` and moves on, never deleting local data (brief scope item 2, offline). */
export class RecoveryNetworkError extends Error {}

export type RecoveryEventType = "recovered" | "completed" | "pending" | "failed";

export interface RecoveryEvent {
  type: RecoveryEventType;
  sessionId: string;
  title: string;
}
